import * as yup from 'yup'
import { log } from './debug'
import type { UploadError } from '@ui/richiesta/pages/contacts/modal-views/hooks/use-upload.hook'
//import { UploadError } from '@ui/stepper/steps/richiesta/components/modal-views/hooks/use-upload.hook.ts'

const regexNumbersAndLetters = /^[A-Za-z0-9]*$/
const regexNumbers = /^[0-9]*$/
const regexPhoneNumber = /^^3[0-9]{8,}$$/
const regexLandlinePhoneNumber = /^0[1-9][0-9]*?$|^$/
const regexNumbersAndDots = /^\d+(\.\d+)*$/
export const regexEmail =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

//CI regex
const ci_old = /^[a-zA-Z]{2}[0-9]{7}$/ // old format
const ci_new = /^[Cc][a-zA-Z][0-9]{5}[a-zA-Z]{2}$/ // new format
const ci_ele = /^[0-9]{7}[a-zA-Z]{2}$/ // electronic format

//Driver license regex
const dl_old = /^[a-zA-Z]{2}[0-9]{7}[a-zA-Z]$/ // old format
const dl_new = /^U1[0-9]{7}[a-zA-Z]$/ // new format
const dl_new_2 = /^U1[a-zA-Z0-9]{7}[a-zA-Z]$/ // new format 2

const currencyFormatter = new Intl.NumberFormat('it-IT', {
	style: 'currency',
	currency: 'EUR',
	minimumFractionDigits: 0,
})

const ERROR_TEMPLATES = {
	onlyNumbersAndLetters: 'Il campo deve contenere soltanto numeri e lettere',
	onlyNumbers: 'Il campo deve contenenere soltanto numeri',
	dateError: 'La data inserita non è valida',
	required: 'Il campo è obbligatorio',
	exceededLenght: (maxLenght: number) =>
		`Il campo deve essere composto da ${maxLenght} caratteri`,
	invalidEmail: "L'indirizzo email è errato",
	range: (max: number, min: number) =>
		`L' importo deve essere compreso tra ${currencyFormatter.format(
			max
		)} e ${currencyFormatter.format(min)}`,
	invalidCAP: 'Il CAP è errato',
	invalidHomeDate: 'Per poter proseguire devi inserire la residenza attuale',
	phoneNumber: 'Il numero di telefono è errato',
	invalidPermessoDate:
		'Per poter proseguire il permesso di soggiorno deve essere in corso di validità',
	invalidDocumentDate:
		'Per poter proseguire devi inserire un documento in corso di validità',
	greaterThanZero: "L'importo deve essere maggiore di 0",
	documentError: 'Il numero del documento di identità è errato',
	invalidAge: 'Devi avere almeno 18 anni per poter proseguire',
	invalidFiscalCode: 'Codice fiscale errato o incoerente con nome e cognome',
	invalidIban: "L'IBAN è errato",
	mobileOverweight: 'La dimensione del file è troppo grande, riprova',
	mobileNotSupported: 'Il formato del file non è supportato, riprova',
}

const UPLOAD_ERROR_TEMPLATES: Record<
	UploadError['type'],
	{ title: string; description: string }
> = {
	SIZE: {
		title: 'La dimensione del file è troppo  grande',
		description:
			'Puoi caricare file di dimensioni non superiori a 5MB, in formato JPG, PNG, TIFF, HEIC/HEIF o PDF.',
	},
	NOT_SUPPORTED: {
		title: 'Il formato del file non è supportato',
		description:
			'Puoi caricare file in formato JPG, PNG, TIFF, HEIC/HEIF o PDF di dimensioni non superiori a 5MB',
	},
	OCR: {
		title: 'La scansione del documento non è andata a buon fine',
		description:
			'Si è verificato un errore durante la scansione del documento. Il sistema OCR non riesce a scansionare correttamente il tuo documento. Controlla che sia ancora valido e che sia ben visibile.',
	},
	GENERIC: {
		title: 'Il caricamento del documento non è andato a buon fine',
		description:
			'Si è verificato un errore nel caricamento del documento. Riprova o cambia modalità e inserisci i tuoi dati manualmente.',
	},
	WRONG_DOCUMENT: {
		title: 'Il documento caricato non è corretto',
		description:
			'Il file fornito non corrisponde al documento richiesto in fase di caricamento',
	},
	CONFIDENCE_LOW: {
		title: 'Impossibile estrarre informazioni dal documento',
		description:
			'Il file fornito non è di qualità sufficiente per una scansione ottimale. Riprova o cambia modalità e inserisci i tuoi dati manualmente.',
	},
	DOCUMENT_EXPIRED: {
		title: 'Documento scaduto',
		description:
			'Il documento che hai fornito è scaduto, non verrà usato per la scansione OCR',
	},
	DOCUMENT_INVALID: {
		title: 'Documento non valido',
		description:
			'Il documento che hai fornito non è valido, non verrà usato per la scansione OCR',
	},
	EXCEEDED_LIMIT: {
		title: 'Limite massimo dei documenti superato',
		description:
			'Puoi allegare massimo due documenti. Riprova ed elimina i file allegati per sostituirli.',
	},
	INVALID_DOCUMENT: {
		title: 'Documento non valido',
		description:
			'I documenti allegati non sono accettabili. Riprova ed elimina i file allegati per sostituirli.',
	},
	NAME_INVALID: {
		title: 'Il nome del file è troppo lungo',
		description: 'Puoi caricare file con un nome non superiore a 100 caratteri',
	},
	NONE: {
		title: '',
		description: '',
	},
}

export const EXT_ALLOWED = [
	'.jpg',
	'.jpeg',
	'.png',
	'.tiff',
	'.heic',
	'.heif',
	'.pdf',
]

export function isEXTAllowed(file: File) {
	return EXT_ALLOWED.includes(`.${file.name.split('.').at(-1)?.toLowerCase()}`)
}

export function isOverWeight(file: File) {
	const ext = file.name.split('.').at(-1)?.toLowerCase()
	if (ext) {
	//if (ext && ext === 'pdf') {
		// overweight check should only be run against a pdf
		return file.size / 1024 / 1024 > 5
	}

	return false
}

export function isNameOverLimit(file: File, limit: number) {
	return file.name.length >= limit
}

function getError(error: UploadError['type']) {
	return {
		type: error,
		title: UPLOAD_ERROR_TEMPLATES[error].title,
		description: UPLOAD_ERROR_TEMPLATES[error].description,
	}
}

function cannotBeEmpty(this: yup.StringSchema) {
	return this.test(
		'is-empty',
		ERROR_TEMPLATES.required,
		(value) => value?.trim() !== ''
	)
}

function testCI(this: yup.StringSchema) {
	return this.test('ci', ERROR_TEMPLATES.documentError, function (value) {
		log('testCI', value)
		if (!value) return
		if (value?.trim() === '') {
			return true
		}

		if (ci_old.test(value) || ci_new.test(value) || ci_ele.test(value)) {
			return true
		}

		return this.createError({
			path: this.path,
			message: ERROR_TEMPLATES.documentError,
		})
	})
}

function testDL(this: yup.StringSchema) {
	return this.test('dl', ERROR_TEMPLATES.documentError, function (value) {
		if (!value) return
		if (value?.trim() === '') {
			return true
		}

		if (
			dl_old.test(value.toUpperCase()) ||
			dl_new.test(value.toUpperCase()) ||
			dl_new_2.test(value.toUpperCase())
		) {
			return true
		}

		return this.createError({
			path: this.path,
			message: ERROR_TEMPLATES.documentError,
		})
	})
}

function testRequired(this: yup.StringSchema) {
	return this.test(
		'required-if-rendered',
		ERROR_TEMPLATES.required,
		function (value) {
			if (this.originalValue !== undefined && value?.trim() === '') {
				return this.createError({
					path: this.path,
					message: ERROR_TEMPLATES.required,
				})
			}

			return true
		}
	)
}

function testRequiredRadio(this: yup.StringSchema) {
	return this.test(
		'required-radio',
		ERROR_TEMPLATES.required,
		function (value) {
			log('testRequiredRadio', this.originalValue, value)
			if (value?.trim() === '') {
				return this.createError({
					path: this.path,
					message: ERROR_TEMPLATES.required,
				})
			}

			return true
		}
	)
}

function testRangeFromString(this: yup.StringSchema, min: number, max: number) {
	return this.test(
		'range-from-string',
		ERROR_TEMPLATES.range(min, max),
		function (value) {
			const number = Number(value)
			if (number > max || number < min) {
				return this.createError({
					path: this.path,
					message: ERROR_TEMPLATES.range(min, max),
				})
			}

			return true
		}
	)
}

export {
	regexNumbersAndLetters,
	regexNumbers,
	regexNumbersAndDots,
	regexPhoneNumber,
	regexLandlinePhoneNumber,
	ERROR_TEMPLATES,
	UPLOAD_ERROR_TEMPLATES,
	testRequired,
	testRangeFromString,
	testRequiredRadio,
	testCI,
	testDL,
	getError,
	cannotBeEmpty,
}
